import { MouseEvent, ChangeEvent, FormEvent, useState } from "react";

import {
  useInput,
  useApisManagersEmployeeConditionSurveysIndex,
  useArray,
  useDropdown,
  useMultiDivisionMultiSectionDropdown,
  useProvidersCurrentEmployee,
} from "~/hooks";

import {
  OptionType,
  ApiManagersEmployeeConditionSurveysIndexRequestType,
  BusinessDivisionType,
  ChildType,
  ParentType,
  SingleValueType,
  EmployeeConditionSurveyType,
} from "~/domains";

import { AllPublicStatus } from "~/domains/PublicStatus";

type ReturnType = {
  employeeConditionSurveys: EmployeeConditionSurveyType[];
  titleKeyword: string;
  hasNextPage?: boolean;
  isLoading: boolean;
  allBusinessSectionsDistribution?: boolean;
  selectableDropdownPublicStatusIds: OptionType[];
  selectedDropdownPublicStatusId?: SingleValueType<OptionType>;
  selectableDropdownDivisions: BusinessDivisionType[];
  selectedDropdownDivisions: BusinessDivisionType[];
  selectableDropdownSections: ChildType[];
  selectedDropdownSections: ChildType[];
  onDivisionChange: (value: ParentType[]) => void;
  onSectionChange: (value: ChildType[]) => void;
  deleteIndexItem: (index: number) => void;
  unshiftItem: (item: EmployeeConditionSurveyType) => void;
  findAndRemove: (func: (item: EmployeeConditionSurveyType) => boolean) => void;
  onChangeDropdownPublicStatusId: (value: SingleValueType<OptionType>) => void;
  onChangeTitleKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  fetchNextPage: () => void;
  onConditionReset: (e: MouseEvent<HTMLButtonElement>) => void;
  onSearchSubmit: (e: FormEvent<HTMLFormElement>) => void;
  setAllBusinessSectionsDistribution: (value: boolean | undefined) => void;
};

export const useSearchManagersEmployeeConditionSurveys = (): ReturnType => {
  const { currentEmployee } = useProvidersCurrentEmployee();
  const [
    { value: titleKeyword, onChange: onChangeTitleKeyword },
    setTitleKeyword,
  ] = useInput("");
  const [allBusinessSectionsDistribution, setAllBusinessSectionsDistribution] =
    useState<boolean | undefined>();
  const [
    {
      dropdownValue: selectedDropdownPublicStatusId,
      onChange: onChangeDropdownPublicStatusId,
      dropdownSelectableValue: selectableDropdownPublicStatusIds,
    },
    setDropdownPublicStatus,
  ] = useDropdown({
    selectableValue: AllPublicStatus.map((status) => ({
      name: status.name,
      id: status.id.toString(),
    })),
  });
  const {
    selectableDivisions,
    selectedDivisions,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSections,
    onSectionChange,
  } = useMultiDivisionMultiSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions || [],
    selectableSections: currentEmployee?.businessSections || [],
  });
  const [params, setParams] =
    useState<ApiManagersEmployeeConditionSurveysIndexRequestType>();

  const { items, setItems, deleteIndexItem, unshiftItem, findAndRemove } =
    useArray<EmployeeConditionSurveyType>([]);

  const { fetchNextPage, hasNextPage, isFetching } =
    useApisManagersEmployeeConditionSurveysIndex({
      params,
      config: {
        onSuccess: (data) => {
          setItems(
            data.pages.map((page) => page.employeeConditionSurveys).flat(),
          );
        },
      },
    });

  const onSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setParams({
      keyword: titleKeyword,
      businessDivisionIds: selectedDivisions.map((division) => division.id),
      businessSectionIds: optionSelectedSections.map((section) => section.id),
    });
  };

  const onConditionReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setTitleKeyword("");
    setDropdownPublicStatus();
    onDivisionChange([]);
    onSectionChange([]);
    setParams({});
    setAllBusinessSectionsDistribution(undefined);
  };

  return {
    employeeConditionSurveys: items,
    titleKeyword,
    isLoading: isFetching,
    allBusinessSectionsDistribution,
    hasNextPage,
    selectableDropdownPublicStatusIds,
    selectedDropdownPublicStatusId,
    selectableDropdownDivisions: selectableDivisions,
    selectedDropdownDivisions: selectedDivisions,
    selectableDropdownSections: optionSelectableSections,
    selectedDropdownSections: optionSelectedSections,
    onDivisionChange,
    onSectionChange,
    unshiftItem,
    deleteIndexItem,
    findAndRemove,
    onChangeDropdownPublicStatusId,
    fetchNextPage,
    onChangeTitleKeyword,
    onConditionReset,
    onSearchSubmit,
    setAllBusinessSectionsDistribution,
  };
};
